import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronUp, GripVertical, Trash2, X } from 'lucide-react'
import {
  DropdownList,
  DropdownItem,
  OpServiceCard,
  OpServiceCardCollapsed,
  OpServiceCardHeader,
  OpServiceCardHeaderLeft,
  OpServiceCardHeaderRight,
  OpServiceCardMain,
  OpServiceCardTitle,
  OpServiceFormGrid,
  OpServiceFormGroup,
  OpServiceLabel,
  OpServiceInput,
  OpServiceAttachmentTrigger,
  OpServiceCircularCollapse,
  OpServiceTextArea,
  OpServiceTimeWrap,
  OpServiceFileList,
  OpServiceFileRow,
  ServiceActionsContainer,
  OrderButton,
} from '../style'
import TimeField from '../../../../../components/TimeField'
import clip from '../../../../../assets/production-flow/rnc/clip.svg'
import { stageLabel } from '../../stageNumbering'

export interface ServiceCardProps {
  service: any
  parentIndex: number
  parentRowKey?: string
  childIndex?: number
  isChild?: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onRemove: () => void
  isEditing: boolean
  searchService: string
  serviceOptions: any[]
  setSearchService: (v: string) => void
  onStartEdit: () => void
  selectItem: (option: any) => void | Promise<void>
  updateItem: (field: string, value: any) => void | Promise<void>
  setFeedback: (v: any) => void
  addServiceFiles: (files: File[]) => void
  downloadPendingServiceFile: (file: File) => void
  removePendingServiceFile: (fileIndex: number) => void
  downloadExistingServiceFile: (serviceStageId: number, fileId: number, fileName: string) => void
  removeExistingServiceFile: (serviceStageId: number, fileId: number) => void
  sortable?: boolean
  canAddChild?: boolean
  onAddChild?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  siblingCount?: number
}

const SortableServiceCard: React.FC<ServiceCardProps> = ({
  service,
  parentIndex,
  parentRowKey,
  childIndex,
  isChild = false,
  isExpanded,
  onToggleExpand,
  onRemove,
  isEditing,
  searchService,
  serviceOptions,
  setSearchService,
  onStartEdit,
  selectItem,
  updateItem,
  setFeedback,
  addServiceFiles,
  downloadPendingServiceFile,
  removePendingServiceFile,
  downloadExistingServiceFile,
  removeExistingServiceFile,
  sortable = false,
  canAddChild = false,
  onAddChild,
  onMoveUp,
  onMoveDown,
  siblingCount = 0,
}) => {
  const sortableId = isChild
    ? `child:${parentRowKey}:${service.rowKey}`
    : `root:${service.rowKey}`

  const sortableHook = useSortable({
    id: sortableId,
    disabled: !sortable,
  })

  const setNodeRef = sortable ? sortableHook.setNodeRef : undefined
  const transform = sortable ? sortableHook.transform : null
  const transition = sortable ? sortableHook.transition : undefined
  const isDragging = sortable ? sortableHook.isDragging : false
  const attributes = sortable ? sortableHook.attributes : {}
  const listeners = sortable ? sortableHook.listeners : {}

  const positionIndex = isChild ? (childIndex ?? 0) : parentIndex
  const canMoveUp = positionIndex > 0
  const canMoveDown = positionIndex < siblingCount - 1

  const style: React.CSSProperties = {
    transform:
      isDragging || !transform
        ? undefined
        : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0 : 1,
    pointerEvents: isDragging ? 'none' : undefined,
  }

  const stageIdForFiles = service.serviceStageId ?? service.id
  const operationLabel = `${stageLabel(parentIndex, childIndex)} - ${service.service || 'Serviço'}`

  const orderActions = (
    <ServiceActionsContainer>
      <OrderButton
        type='button'
        disabled={!canMoveUp || !onMoveUp}
        onClick={(e) => {
          e.stopPropagation()
          onMoveUp?.()
        }}
        title='Subir'
      >
        <ChevronUp size={18} />
      </OrderButton>
      <OrderButton
        type='button'
        disabled={!canMoveDown || !onMoveDown}
        onClick={(e) => {
          e.stopPropagation()
          onMoveDown?.()
        }}
        title='Descer'
      >
        <ChevronDown size={18} />
      </OrderButton>
      <OrderButton
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        title='Excluir'
      >
        <Trash2 size={18} />
      </OrderButton>
    </ServiceActionsContainer>
  )

  const collapseControl = (
    <OpServiceCircularCollapse
      type='button'
      onClick={(e) => {
        e.stopPropagation()
        onToggleExpand()
      }}
      title={isExpanded ? 'Recolher' : 'Expandir'}
    >
      <ChevronDown
        size={16}
        color='#666'
        style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
      />
    </OpServiceCircularCollapse>
  )

  const openFilePicker = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = '*/*'
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement
      const files = Array.from(target.files || []) as File[]
      const currentFiles = service.pendingFiles || []
      if (currentFiles.length + files.length > 5) {
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Máximo de 5 arquivos por serviço.',
        })
        return
      }
      addServiceFiles(files)
    }
    input.click()
  }

  const renderFileLists = () => (
    <>
      {!!service.pendingFiles?.length && (
        <OpServiceFileList>
          {service.pendingFiles.map((file: File, fileIndex: number) => (
            <OpServiceFileRow key={`${file.name}-${fileIndex}`}>
              <span style={{ wordBreak: 'break-word', marginRight: 10 }}>{file.name}</span>
              <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                <button
                  type='button'
                  onClick={() => downloadPendingServiceFile(file)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#0090d3',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 13,
                  }}
                >
                  Download
                </button>
                <button
                  type='button'
                  onClick={() => removePendingServiceFile(fileIndex)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            </OpServiceFileRow>
          ))}
        </OpServiceFileList>
      )}
      {!!service.existingFiles?.length && (
        <OpServiceFileList>
          {service.existingFiles.map((file: { id: number; name: string }) => (
            <OpServiceFileRow key={file.id}>
              <span style={{ wordBreak: 'break-word', marginRight: 10 }}>{file.name}</span>
              <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                <button
                  type='button'
                  onClick={() =>
                    downloadExistingServiceFile(stageIdForFiles, file.id, file.name)
                  }
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#0090d3',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 13,
                  }}
                >
                  Download
                </button>
                <button
                  type='button'
                  onClick={() => removeExistingServiceFile(stageIdForFiles, file.id)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            </OpServiceFileRow>
          ))}
        </OpServiceFileList>
      )}
    </>
  )

  if (!isExpanded) {
    return (
      <OpServiceCardCollapsed ref={setNodeRef} style={style} $isChild={isChild}>
        <OpServiceCardHeaderLeft {...attributes} {...listeners}>
          {sortable && <GripVertical size={16} color='#666' />}
          <OpServiceCardTitle>{operationLabel}</OpServiceCardTitle>
        </OpServiceCardHeaderLeft>
        <OpServiceCardHeaderRight>
          {collapseControl}
        </OpServiceCardHeaderRight>
        {orderActions}
      </OpServiceCardCollapsed>
    )
  }

  return (
    <OpServiceCard
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      $isChild={isChild}
    >
      <OpServiceCardMain>
        <OpServiceCardHeader>
          <OpServiceCardHeaderLeft {...attributes} {...listeners}>
            {sortable && <GripVertical size={16} color='#666' />}
            <OpServiceCardTitle>{operationLabel}</OpServiceCardTitle>
          </OpServiceCardHeaderLeft>
          <OpServiceCardHeaderRight>{collapseControl}</OpServiceCardHeaderRight>
        </OpServiceCardHeader>

        <OpServiceFormGrid $columns={2}>
          <OpServiceFormGroup className='dropdown-container' style={{ gridColumn: '1 / -1' }}>
            <OpServiceLabel>Serviço</OpServiceLabel>
            <OpServiceInput
              type='text'
              value={isEditing ? searchService : service.service}
              onChange={(e) => {
                setSearchService(e.target.value)
                onStartEdit()
              }}
              onFocus={() => {
                onStartEdit()
                setSearchService(service.service)
              }}
              placeholder='Filtre por nome'
            />
            {isEditing && serviceOptions.length > 0 && (
              <DropdownList>
                {serviceOptions.map((option) => (
                  <DropdownItem key={option.id} onClick={() => selectItem(option)}>
                    {option.name}
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </OpServiceFormGroup>

          <OpServiceFormGroup>
            <OpServiceLabel>Tempo de execução</OpServiceLabel>
            <OpServiceTimeWrap>
              <TimeField
                valueSeconds={service.executionTime ?? null}
                onChangeSeconds={(seconds) => updateItem('executionTime', seconds)}
              />
            </OpServiceTimeWrap>
          </OpServiceFormGroup>

          <OpServiceFormGroup>
            <OpServiceLabel>Tempo de setup</OpServiceLabel>
            <OpServiceTimeWrap>
              <TimeField
                valueSeconds={service.setupTime ?? null}
                onChangeSeconds={(seconds) => updateItem('setupTime', seconds)}
              />
            </OpServiceTimeWrap>
          </OpServiceFormGroup>
        </OpServiceFormGrid>

        <OpServiceFormGrid $columns={1}>
          <OpServiceFormGroup>
            <OpServiceLabel>Anexo</OpServiceLabel>
            <OpServiceAttachmentTrigger
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openFilePicker()
                }
              }}
              onClick={openFilePicker}
            >
              <span>
                {(service.pendingFiles || []).length > 0 ||
                (service.existingFiles || []).length > 0
                  ? `${(service.pendingFiles || []).length + (service.existingFiles || []).length} arquivo(s)`
                  : 'Clique para adicionar arquivos'}
              </span>
              <img src={clip} alt='Anexo' width={18} height={18} />
            </OpServiceAttachmentTrigger>
            {renderFileLists()}
          </OpServiceFormGroup>
        </OpServiceFormGrid>

        <OpServiceFormGrid $columns={2}>
          <OpServiceFormGroup>
            <OpServiceLabel>Descrição</OpServiceLabel>
            <OpServiceTextArea
              value={service.description}
              onChange={(e) => updateItem('description', e.target.value)}
              placeholder='Digite a descrição'
              rows={3}
            />
          </OpServiceFormGroup>
          <OpServiceFormGroup>
            <OpServiceLabel>Observação</OpServiceLabel>
            <OpServiceTextArea
              value={service.observation}
              onChange={(e) => updateItem('observation', e.target.value)}
              placeholder='Digite a observação'
              rows={3}
            />
          </OpServiceFormGroup>
        </OpServiceFormGrid>

        {!isChild && canAddChild && onAddChild && (
          <button
            type='button'
            onClick={onAddChild}
            style={{
              marginTop: 8,
              border: '1px solid #0090d3',
              background: 'transparent',
              color: '#0090d3',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Adicionar subetapa
          </button>
        )}
      </OpServiceCardMain>

      {orderActions}
    </OpServiceCard>
  )
}

export function ServiceStageDragOverlay({ label }: { label: string }) {
  return (
    <OpServiceCardCollapsed
      style={{
        cursor: 'grabbing',
        width: '100%',
        minWidth: 320,
        maxWidth: '100%',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        opacity: 1,
      }}
    >
      <OpServiceCardHeaderLeft style={{ cursor: 'grabbing' }}>
        <GripVertical size={16} color='#666' />
        <OpServiceCardTitle>{label}</OpServiceCardTitle>
      </OpServiceCardHeaderLeft>
    </OpServiceCardCollapsed>
  )
}

export default SortableServiceCard
